import { Injectable } from '@angular/core';
import { EmpresaCadastralResponse } from '@interfaces/corporate-registry/responses/empresa-cadastral.response';
import {
  RedeLayout,
  RedeProfundidade,
  VisaoArvoreResponse,
  VisaoGrafoResponse,
} from '@interfaces/corporate-registry/responses/rede-societaria.response';
import {
  VisaoControleResponse,
  VisaoEstruturaResponse,
  VisaoGeoResponse,
  VisaoMatrizResponse,
  VisaoRelacoesResponse,
} from '@interfaces/corporate-registry/responses/rede-visoes.response';
import { VaultApiService } from '@services/api/vault-api.service';
import { Observable } from 'rxjs';

/**
 * Registro empresarial da Receita Federal (cadastro, QSA e rede societária), servido pelo backend
 * como proxy do Vault-Dossiê.
 *
 * ⚠️ A permissão é `CorporateRegistryPermissions.VIEW`, que hoje **não** inclui o papel de parceiro:
 * para ele estas rotas respondem 403, e quem chama precisa tratar isso como "sem acesso", não como
 * falha.
 */
@Injectable({ providedIn: 'root' })
export class CorporateRegistryHttpService {
  private readonly BASE_URL = 'corporate-registry';

  constructor(private readonly apiService: VaultApiService) {}

  /** Cadastro completo e QSA. `cnpj` com ou sem máscara — o backend normaliza. */
  getEmpresa(cnpj: string): Observable<EmpresaCadastralResponse | null> {
    return this.apiService.get<EmpresaCadastralResponse>(`${this.BASE_URL}/empresas/${cnpj}`);
  }

  /**
   * Rede em nós e arestas. Com `layout`, os nós já vêm com `x`/`y`; com `null` (layout Conexões),
   * só a topologia — a posição é calculada no cliente.
   */
  getRedeGrafo(
    cnpj: string,
    prof: RedeProfundidade,
    layout: RedeLayout | null
  ): Observable<VisaoGrafoResponse | null> {
    return this.apiService.get<VisaoGrafoResponse>(
      `${this.BASE_URL}/empresas/${cnpj}/rede/grafo`,
      undefined,
      layout ? { prof, layout } : { prof }
    );
  }

  /** Grade sócio × empresa. O backend limita a grade e informa o que ficou fora em `ocultos`. */
  getRedeMatriz(cnpj: string, prof: RedeProfundidade): Observable<VisaoMatrizResponse | null> {
    return this.apiService.get<VisaoMatrizResponse>(
      `${this.BASE_URL}/empresas/${cnpj}/rede/matriz`,
      undefined,
      { prof }
    );
  }

  /** Cada empresa do recorte com os vínculos identificados. */
  getRedeRelacoes(cnpj: string, prof: RedeProfundidade): Observable<VisaoRelacoesResponse | null> {
    return this.apiService.get<VisaoRelacoesResponse>(
      `${this.BASE_URL}/empresas/${cnpj}/rede/relacoes`,
      undefined,
      { prof }
    );
  }

  /** PJs que são sócias de duas ou mais empresas do recorte ("possível holding"). */
  getRedeEstrutura(
    cnpj: string,
    prof: RedeProfundidade
  ): Observable<VisaoEstruturaResponse | null> {
    return this.apiService.get<VisaoEstruturaResponse>(
      `${this.BASE_URL}/empresas/${cnpj}/rede/estrutura`,
      undefined,
      { prof }
    );
  }

  /**
   * Estruturas de controle, cada uma com o próprio grafo. `layout=hierarquico` fixo: sem ele o grafo
   * de cada estrutura vem sem coordenadas.
   */
  getRedeControle(cnpj: string, prof: RedeProfundidade): Observable<VisaoControleResponse | null> {
    return this.apiService.get<VisaoControleResponse>(
      `${this.BASE_URL}/empresas/${cnpj}/rede/controle`,
      undefined,
      { prof, layout: 'hierarquico' }
    );
  }

  /**
   * Empresas geolocalizadas.
   *
   * ⚠️ **Custa dinheiro**: o backend geocodifica cada endereço novo no Google (com cache). Chame
   * só quando a pessoa abrir a visão Geo, e sempre através do `CorporateRegistryCacheService`.
   */
  getRedeGeo(cnpj: string, prof: RedeProfundidade): Observable<VisaoGeoResponse | null> {
    return this.apiService.get<VisaoGeoResponse>(
      `${this.BASE_URL}/empresas/${cnpj}/rede/geo`,
      undefined,
      { prof }
    );
  }

  /** Rede em árvore recursiva: empresa → sócios → empresas do sócio. */
  getRedeArvore(cnpj: string, prof: RedeProfundidade): Observable<VisaoArvoreResponse | null> {
    return this.apiService.get<VisaoArvoreResponse>(
      `${this.BASE_URL}/empresas/${cnpj}/rede/arvore`,
      undefined,
      { prof }
    );
  }
}
